import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { api, setAccessToken } from '../api/client';
import type { User, DelegatedAccount } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  delegatedAccounts: DelegatedAccount[];
  activeAccountId: string | null;
  setActiveAccountId: (id: string | null) => void;
  login: (email: string, password: string, totpCode?: string) => Promise<{ mfaRequired?: boolean }>;
  register: (name: string, email: string, password: string, acceptedTerms: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [delegatedAccounts, setDelegatedAccounts] = useState<DelegatedAccount[]>([]);
  const [activeAccountId, setActiveAccountIdState] = useState<string | null>(
    localStorage.getItem('activeAccountId')
  );

  function fetchDelegatedAccounts() {
    api.get('/account/delegated').then(({ data }) => setDelegatedAccounts(Array.isArray(data) ? data : [])).catch(() => {});
  }

  useEffect(() => {
    api.post('/auth/refresh', {}, { timeout: 3000 })
      .then(({ data }) => {
        setAccessToken(data.accessToken);
        return api.get('/auth/me', { timeout: 3000 });
      })
      .then(({ data }) => {
        setUser(data);
        fetchDelegatedAccounts();
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // If the visitor arrived via an email invite but chose to sign in with an existing
  // account instead of creating a new password (see AcceptInvite.tsx), the invite
  // token is stashed here beforehand. Redeeming it now attaches the invited event/
  // circle to this already-existing account instead of a disconnected new identity.
  async function acceptPendingInvite() {
    const token = sessionStorage.getItem('pendingInviteToken');
    if (!token) return;
    sessionStorage.removeItem('pendingInviteToken');
    await api.post('/account/members/accept-invite', { token }).catch(() => {});
  }

  async function login(email: string, password: string, totpCode?: string) {
    const { data } = await api.post('/auth/login', { email, password, totpCode });
    if (data.mfaRequired) return { mfaRequired: true };
    setAccessToken(data.accessToken);
    setUser(data.user);
    await acceptPendingInvite();
    fetchDelegatedAccounts();
    return {};
  }

  async function register(name: string, email: string, password: string, acceptedTerms: boolean) {
    const { data } = await api.post('/auth/register', { name, email, password, acceptedTerms });
    setAccessToken(data.accessToken);
    setUser(data.user);
    fetchDelegatedAccounts();
  }

  async function logout() {
    await api.post('/auth/logout').catch(() => {});
    setAccessToken(null);
    setUser(null);
    localStorage.removeItem('activeAccountId');
    setActiveAccountIdState(null);
    setDelegatedAccounts([]);
  }

  async function refresh() {
    const { data } = await api.get('/auth/me');
    setUser(data);
    fetchDelegatedAccounts();
  }

  function setActiveAccountId(id: string | null) {
    if (id) localStorage.setItem('activeAccountId', id);
    else localStorage.removeItem('activeAccountId');
    setActiveAccountIdState(id);
  }

  return (
    <AuthContext.Provider value={{ user, loading, delegatedAccounts, activeAccountId, setActiveAccountId, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
